import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { resourceService } from '../services/resourceService';
import type { Resource } from '../types';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { logger } from '../utils/logger';


interface ResourceDownloadProps {
  resource: Resource;
  onDownloadStart?: () => void;
  onDownloadComplete?: (resource: Resource) => void;
  onDownloadError?: (error: string) => void;
  variant?: 'button' | 'icon' | 'text';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export const ResourceDownload: React.FC<ResourceDownloadProps> = ({
  resource,
  onDownloadStart,
  onDownloadComplete,
  onDownloadError,
  variant = 'button',
  size = 'medium',
  disabled = false
}) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const handleDownload = async () => {
    if (downloading || disabled) return;

    setDownloading(true);
    setDownloadProgress(0);
    setError(null);
    setSuccess(false);
    onDownloadStart?.();

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 20;
        });
      }, 200);

      // Download the resource
      const blob = await resourceService.downloadResource(resource.id);
      
      // Complete progress
      clearInterval(progressInterval);
      setDownloadProgress(100);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setDownloading(false);
        setDownloadProgress(0);
        setSuccess(true);
        showSuccess('Đã tải xuống tệp thành công!');
        onDownloadComplete?.(resource);
      }, 500);

    } catch (err) {
      setDownloading(false);
      setDownloadProgress(0);
      const errorMessage = err instanceof Error ? err.message : 'Failed to download resource';
      setError(errorMessage);
      showError(errorMessage);
      onDownloadError?.(errorMessage);
      logger.error('Download error:', err);
    }
  };

  const handleRetry = () => {
    setError(null);
    setSuccess(false);
    handleDownload();
  };


  const getButtonContent = () => {
    if (downloading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Downloading... {Math.round(downloadProgress)}%
          </Typography>
        </Box>
      );
    }

    if (success) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="body2">Downloaded</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          <Typography variant="body2">Error</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DownloadIcon />
        <Typography variant="body2">Download</Typography>
      </Box>
    );
  };

  const renderButton = () => {
    const commonProps = {
      onClick: handleDownload,
      disabled: downloading || disabled,
      size: size as any
    } as const;

    switch (variant) {
      case 'icon':
        return (
          <Tooltip title={downloading ? 'Downloading...' : 'Download'}>
            <IconButton onClick={handleDownload} disabled={downloading || disabled} size={size as any} color="primary">
              {downloading ? <RefreshIcon /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
        );
      
      case 'text':
        return (
          <Button {...commonProps} startIcon={downloading ? undefined : <DownloadIcon />} variant="text" color="primary">
            {getButtonContent()}
          </Button>
        );
      
      default:
        return (
          <Button {...commonProps} startIcon={downloading ? undefined : <DownloadIcon />} variant="contained" color="primary">
            {getButtonContent()}
          </Button>
        );
    }
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        {renderButton()}
        
        {downloading && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={downloadProgress} 
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        )}

        {error && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Alert severity="error" sx={{ flexGrow: 1 }}>
              {error}
            </Alert>
            <Button size="small" onClick={handleRetry}>
              Retry
            </Button>
          </Box>
        )}
      </Box>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </>
  );
};

export default ResourceDownload;
